// Compose + send an email as the current user (Phase 5 Gmail). Mirrors the
// NewTaskModal overlay. Sends via gmailSend itself; the shared-timeline write
// happens through onLogEmail ONLY after the send truly succeeded, and a
// failed log never reads as a send failure (the email already went out).
//
// Wave 2 (register item 9): an optional canned-template dropdown. Picking a
// template RESOLVES its merge fields immediately and drops the finished text
// into the fields, so the sender always reads the real message before sending —
// nothing is substituted behind their back at send time.
//
// Templates v2: the dropdown is grouped by workflow (<optgroup>, in step
// order); a template's default Cc/Bcc prefill editable fields; free-text
// `[[blanks]]` must be filled in before Send unlocks; and "Open in Gmail"
// hands the current draft to Gmail's own composer as an alternative to Send
// (allowed with blanks remaining — they can finish it there).
import { useMemo, useState } from 'react'
import { gmailSend, gmailComposeUrl, normalizeAddressList } from '../lib/google.js'
import {
  buildMergeContext, draftFromTemplate, templatesForType, warningFor,
  groupTemplates, remainingPlaceholders, placeholderWarning,
} from '../lib/mergeFields.js'
import { engagementType } from '../lib/format.js'
import { Modal, Button, Field } from '../ds/index.js'

const optionLabel = (t) => (t.step ? `${t.step} — ${t.name}` : t.name)

export default function ComposeEmailModal({
  to, recipients = [], onClose, onLogEmail,
  templates = [], contact = null, engagement = null, sender = null,
}) {
  // `recipients` (engagement records) offers a picker; `to` is the single-contact
  // case. One To address per send; Cc/Bcc carry everyone else.
  const [chosenTo, setChosenTo] = useState(to || recipients[0]?.email || '')
  const [cc, setCc] = useState('')
  const [bcc, setBcc] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [warning, setWarning] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)

  // Recomputed from the live text, so the warning clears as blanks are filled.
  const blanks = useMemo(() => remainingPlaceholders({ subject, body }), [subject, body])
  const valid = chosenTo && subject.trim() && body.trim() && blanks.length === 0 && !sending

  const offered = useMemo(
    () => templatesForType(templates, engagement ? engagementType(engagement) : null),
    [templates, engagement],
  )
  const groups = useMemo(() => groupTemplates(offered), [offered])

  const pickedContact = useMemo(
    () => contact || recipients.find((r) => r.email === chosenTo) || null,
    [contact, recipients, chosenTo],
  )

  const applyTemplate = (id) => {
    setTemplateId(id)
    if (!id) { setWarning(''); return }
    const t = offered.find((x) => x.id === id)
    if (!t) return
    const draft = draftFromTemplate(t, buildMergeContext({ contact: pickedContact, engagement, sender }))
    setSubject(draft.subject)
    setBody(draft.body)
    setCc(draft.cc)
    setBcc(draft.bcc)
    setWarning(warningFor(draft))
  }

  const send = async () => {
    if (!valid) return
    setSending(true)
    setError(null)
    const ccList = normalizeAddressList(cc)
    const bccList = normalizeAddressList(bcc)
    try {
      const resp = await gmailSend({ to: chosenTo, cc: ccList, bcc: bccList, subject: subject.trim(), body })
      try {
        await onLogEmail({ to: chosenTo, cc: ccList, bcc: bccList, subject: subject.trim(), body, gmailMessageId: resp?.id || null, threadId: resp?.threadId || null })
      } catch { /* timeline write is best-effort — the email is already sent */ }
      onClose()
    } catch (e) {
      setError(e.message)
      setSending(false)
    }
  }

  // Not logged: nothing has been sent yet, and we can't know whether it will be.
  const openInGmail = () => {
    const url = gmailComposeUrl({ to: chosenTo, cc, bcc, subject: subject.trim(), body })
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const closeIfNotSending = () => { if (!sending) onClose() }
  const selectStyle = { background: 'var(--color-bg-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 9px', color: 'var(--color-white)', fontSize: 12.5, width: '100%' }
  const noteStyle = { fontSize: 11.5, color: 'var(--color-gold)', background: 'var(--color-bg-dark)', border: '1px solid var(--color-divider-dark)', borderRadius: 'var(--radius-sm)', padding: '7px 9px' }

  return (
    <Modal
      onClose={closeIfNotSending}
      title="New email"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={closeIfNotSending}>Cancel</Button>
          <Button variant="ghost" onClick={openInGmail} disabled={sending || !chosenTo} title="Open this draft in Gmail to finish and send it there (not logged here)">Open in Gmail</Button>
          <Button onClick={send} disabled={!valid} title={blanks.length ? 'Fill in the [[blanks]] first' : undefined}>{sending ? 'Sending…' : 'Send'}</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {recipients.length > 0 ? (
          <div>
            <span className="cn-field-label">To</span>
            <select value={chosenTo} onChange={(e) => setChosenTo(e.target.value)} style={{ ...selectStyle, marginTop: 6 }}>
              {recipients.map((r) => (
                <option key={r.id} value={r.email}>{`${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email} — {r.email}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 4 }}>Sent from your Gmail, logged to this record. One recipient per send.</div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>To <span className="font-mono" style={{ color: 'var(--color-white)' }}>{chosenTo}</span> — sent from your Gmail, logged to this record.</div>
        )}

        {offered.length > 0 && (
          <div>
            <span className="cn-field-label">Template</span>
            <select value={templateId} onChange={(e) => applyTemplate(e.target.value)} style={{ ...selectStyle, marginTop: 6 }}>
              <option value="">Start from scratch</option>
              {groups.map((g) => (
                <optgroup key={g.workflow || '~other'} label={g.workflow || 'Other'}>
                  {g.templates.map((t) => <option key={t.id} value={t.id}>{optionLabel(t)}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
        )}

        {warning && <div style={noteStyle}>Check before sending — {warning}</div>}
        {blanks.length > 0 && <div style={noteStyle} role="status">{placeholderWarning(blanks)} — replace each before sending.</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Field value={cc} onChange={(e) => setCc(e.target.value)} placeholder="Cc" aria-label="Cc" />
          <Field value={bcc} onChange={(e) => setBcc(e.target.value)} placeholder="Bcc" aria-label="Bcc" />
        </div>
        <Field value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" />
        <Field as="textarea" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your email…" rows={9} style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
        {error && <div style={{ fontSize: 12, color: 'var(--color-danger)' }}>{error}</div>}
      </div>
    </Modal>
  )
}
